import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { useQueryClient } from '@tanstack/react-query';
import {
  getMeetingPublicPublicMeetingPublicIdGet,
  getSessionInfoPublicPublicSessionsRoomIdInfoGet,
  getSessionInfoPublicPublicSessionsRoomIdInfoGetQueryKey,
} from '@/api';
import type { GetSessionInfoResponse } from '@/api';
import RoomScene from '@/scenes/Room/RoomScene';
import meetingCss from '@/styles/meeting.css?url';
import Loader from '@/components/Loader';

export const Route = createFileRoute('/meeting/$roomPublicId/')({
  component: RouteComponent,
  validateSearch: z.object({
    debug: z.coerce.boolean().optional(),
    test: z.coerce.boolean().optional(),
    templateId: z.string().optional(),
    imageDemo: z.coerce.boolean().optional(),
    /** Opened inside the website widget's iframe. */
    embed: z.coerce.boolean().optional(),
  }),
  loaderDeps: ({ search }) => ({ imageDemo: search.imageDemo, embed: search.embed }),
  loader: async ({ params, deps }) => {
    if (deps.imageDemo) {
      return { id: 'demo', public_id: params.roomPublicId } as any;
    }
    const room = await getMeetingPublicPublicMeetingPublicIdGet({
      path: { public_id: params.roomPublicId },
    });
    if (!deps.embed || !room.data?.id) return room.data;
    // Website widget: the call starts by itself, so have the session settings ready with the room.
    const info = await getSessionInfoPublicPublicSessionsRoomIdInfoGet({ path: { room_id: room.data.id } });
    return { ...room.data, embed_info: (info.data ?? null) as GetSessionInfoResponse | null };
  },
  head: ({ loaderData, params }) => {
    const room = loaderData;
    const roomLink = `https://app.greetai.co/meeting/${params.roomPublicId}`;

    return {
      title: 'Session',
      meta: [
        {
          name: 'title',
          property: 'og:title',
          content: 'Session',
        },
        {
          name: 'description',
          property: 'og:description',
          content: 'Session',
        },
        {
          property: 'og:url',
          content: roomLink,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          property: 'twitter:domain',
          content: 'greetai.co',
        },
        {
          property: 'twitter:url',
          content: roomLink,
        },
      ],
      links: [
        {
          rel: 'stylesheet',
          href: meetingCss,
        },
      ],
    };
  },
});

function RouteComponent() {
  const room = Route.useLoaderData();
  const { debug, test, templateId } = Route.useSearch();
  const queryClient = useQueryClient();
  const embedInfo = (room as { embed_info?: GetSessionInfoResponse | null } | undefined)?.embed_info ?? null;
  // Seed the session info the call reads, so it starts without waiting on a second request.
  useState(() => {
    if (embedInfo && room?.id) {
      queryClient.setQueryData(
        getSessionInfoPublicPublicSessionsRoomIdInfoGetQueryKey({ path: { room_id: room.id } }),
        embedInfo,
      );
    }
    return null;
  });

  // Not in meeting.css: route stylesheets stay in <head> after leaving the route, so a body rule there leaks to every page after.
  useEffect(() => {
    document.body.style.backgroundColor = '#333';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  if (!room) {
    return <Loader />;
  }

  return <RoomScene isTest={test} testTemplateId={templateId} debug={debug} />;
}
